import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { PreferencesService } from '../service/preferencesService';
import { createDefaultTenantPreferences } from '../utils/preferencesDefaults';

/**
 * @typedef {import('../types/preferences').BusinessPreferences} BusinessPreferences
 */

/**
 * Default preferences - fallback while loading from backend
 * When a tenant has not configured preferences, the backend returns these system defaults
 * @type {Partial<BusinessPreferences>}
 */
const DEFAULT_PREFERENCES = createDefaultTenantPreferences();

/**
 * Preferences Context for app-wide preferences management
 * @type {React.Context<{preferences: Partial<BusinessPreferences>, loading: boolean, error: string|null}>}
 */
const PreferencesContext = createContext({
    preferences: DEFAULT_PREFERENCES,
    loading: true,
    error: null
});

/**
 * Preferences Provider Component
 * Fetches tenant preferences from backend API on mount
 * Backend returns system defaults if tenant has not configured preferences yet
 * Should be placed high in the component tree (e.g., App.js)
 */
export const PreferencesProvider = ({ children }) => {
    const authContext = useAuth();
    const user = authContext?.user;
    
    const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!user) {
            setLoading(false);
            setPreferences(DEFAULT_PREFERENCES);
            return;
        }

        const fetchBusinessPreferences = async () => {
            try {
                setLoading(true);
                setError(null);

                // Check localStorage first for cached preferences
                const cacheKey = `preferences_${user.tenantId}`;
                const cached = localStorage.getItem(cacheKey);
                
                if (cached) {
                    const cachedPreferences = JSON.parse(cached);
                    setPreferences({ ...DEFAULT_PREFERENCES, ...cachedPreferences });
                    setLoading(false);
                    // Still fetch fresh data in background
                    fetchFromBackend();
                    return;
                }

                // Fetch from backend
                await fetchFromBackend();
            } catch (err) {
                console.error('Failed to initialize preferences:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        const fetchFromBackend = async () => {
            try {
                const response = await PreferencesService.getBusinessPreferences({suppressErrorToast: true});
                
                if (response && response.data) {
                    const apiPreferences = response.data;

                    // Merge with defaults to ensure all required fields exist
                    const mergedPreferences = { ...DEFAULT_PREFERENCES, ...apiPreferences };

                    // Cache in localStorage
                    const cacheKey = `preferences_${user.tenantId}`;
                    localStorage.setItem(
                        cacheKey,
                        JSON.stringify(mergedPreferences)
                    );

                    setPreferences(mergedPreferences);
                    setError(null);
                }
            } catch (err) {
                // If preferences do not yet exist for a brand-new tenant, backend returns system defaults.
                // No error handling needed - just use what we get
                console.error('Failed to fetch business preferences:', err);
                if (!preferences || Object.keys(preferences).length === 0) {
                    setError(err.message);
                }
            } finally {
                setLoading(false);
            }
        };

        if (user?.tenantId) {
            fetchBusinessPreferences();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    return (
        <PreferencesContext.Provider value={{ preferences, loading, error }}>
            {children}
        </PreferencesContext.Provider>
    );
};

/**
 * Hook to use business preferences from context
 * Returns preferences without loading delay since preferences are pre-loaded
 * @returns {{preferences: Partial<BusinessPreferences>, loading: boolean, error: string|null}}
 */
export const usePreferences = () => {
    const context = useContext(PreferencesContext);
    if (!context) {
        throw new Error('usePreferences must be used within a PreferencesProvider');
    }
    return context;
};

/**
 * Alias for usePreferences - for backward compatibility
 * @deprecated Use usePreferences instead
 */
export const useBusinessPreferences = usePreferences;

/**
 * Hook to save business/tenant preferences
 * @returns {Object} - { saveBusinessPreferences, saving, error }
 */
export const useSaveBusinessPreferences = () => {
    const authContext = useAuth();
    const user = authContext?.user;
    
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const saveBusinessPreferences = async (preferences) => {
        if (!user?.tenantId) {
            throw new Error('User not authenticated or missing tenant ID');
        }

        try {
            setSaving(true);
            setError(null);
            const response = await PreferencesService.updateBusinessPreferences(preferences);

            if (response && response.data) {
                // Update cache
                localStorage.setItem(
                    `preferences_${user.tenantId}`,
                    JSON.stringify(response.data)
                );
            }

            return response;
        } catch (err) {
            console.error('Failed to save business preferences:', err);
            setError(err.message);
            throw err;
        } finally {
            setSaving(false);
        }
    };

    return { saveBusinessPreferences, saving, error };
};

/**
 * Hook to save location-specific preferences
 * @param {number} locationId - The location ID
 * @returns {Object} - { saveLocationPreferences, saving, error }
 */
export const useSaveLocationPreferences = (locationId) => {
    const authContext = useAuth();
    const user = authContext?.user;
    
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const saveLocationPreferences = async (preferences) => {
        if (!user?.tenantId) {
            throw new Error('User not authenticated or missing tenant ID');
        }

        try {
            setSaving(true);
            setError(null);

            const response = await PreferencesService.saveLocationPreferences(user.tenantId, locationId, preferences);

            if (response && response.data) {
                // Update cache
                localStorage.setItem(
                    `preferences_location_${user.tenantId}_${locationId}`,
                    JSON.stringify(response.data)
                );
            }

            return response;
        } catch (err) {
            console.error('Failed to save location preferences:', err);
            setError(err.message);
            throw err;
        } finally {
            setSaving(false);
        }
    };

    return { saveLocationPreferences, saving, error };
};

/**
 * Hook to get location-specific preferences (with fallback to tenant defaults)
 * @param {number} locationId - The location ID
 * @returns {Object} - { locationPreferences, loading, error }
 */
export const useLocationPreferences = (locationId) => {
    const authContext = useAuth();
    const user = authContext?.user;
    
    const [locationPreferences, setLocationPreferences] = useState(DEFAULT_PREFERENCES);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!user?.tenantId || !locationId) {
            setLoading(false);
            return;
        }

        const fetchLocationPreferences = async () => {
            try {
                setLoading(true);
                setError(null);

                // Check cache first
                const cacheKey = `preferences_location_${user.tenantId}_${locationId}`;
                const cached = localStorage.getItem(cacheKey);
                if (cached) {
                    const cachedPreferences = JSON.parse(cached);
                    setLocationPreferences({ ...DEFAULT_PREFERENCES, ...cachedPreferences });
                    setLoading(false);
                    // Still fetch fresh data in background
                    fetchFromBackend();
                    return;
                }

                // Fetch from backend
                fetchFromBackend();
            } catch (err) {
                console.error('Failed to fetch location preferences:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        const fetchFromBackend = async () => {
            try {
                const response = await PreferencesService.getLocationPreferences(user.tenantId, locationId);

                if (response && response.data) {
                    const mergedPreferences = { ...DEFAULT_PREFERENCES, ...response.data };

                    // Cache in localStorage
                    localStorage.setItem(
                        `preferences_location_${user.tenantId}_${locationId}`,
                        JSON.stringify(mergedPreferences)
                    );

                    setLocationPreferences(mergedPreferences);
                    setError(null);
                }
            } catch (err) {
                console.error('Failed to fetch location preferences from backend:', err);
                if (!locationPreferences || Object.keys(locationPreferences).length === 0) {
                    setError(err.message);
                }
            } finally {
                setLoading(false);
            }
        };

        if (user?.tenantId && locationId) {
            fetchLocationPreferences();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.tenantId, locationId]);

    return { locationPreferences, loading, error };
};

